// Whether an element's text is cut off by its own box (a line clamp or an ellipsis), so the
// caller can offer the full text, as the product card's name does with a tooltip. The ref is
// a callback ref, so an element that mounts later (a card switching to the grid layout) is
// measured too: after layout, and again whenever it resizes (a viewport change moves the
// clamp). False where ResizeObserver is absent (happy-dom), and while no element is attached.

import { useCallback, useLayoutEffect, useState } from 'react';

function overflows(element: HTMLElement): boolean {
  return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
}

export function useTextTruncated<T extends HTMLElement>(
  text: string,
): readonly [(element: T | null) => void, boolean] {
  const [element, setElement] = useState<T | null>(null);
  const [truncated, setTruncated] = useState(false);
  const ref = useCallback((next: T | null) => setElement(next), []);

  useLayoutEffect(() => {
    if (element === null || typeof ResizeObserver === 'undefined') {
      setTruncated(false);
      return;
    }
    const measure = () => setTruncated(overflows(element));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, text]);

  return [ref, truncated];
}
